/**
 * Tenant admin API client — #2366.
 *
 * Thin client over the tenancy routes (#2364, #2365):
 *
 *   GET  /api/tenant/integrations                              current tenant's integration status
 *   PUT  /api/tenant/integrations/shopify                      verify + save Shopify credentials
 *   PUT  /api/tenant/integrations/onfleet                      verify + save Onfleet credentials
 *   POST /api/tenant/integrations/{provider}/register-webhooks create missing subscriptions
 *   GET  /api/tenant/integrations/{provider}/webhooks          list subscriptions (#2385)
 *   GET  /api/tenant/settings                                  settings + their source
 *   PUT  /api/tenant/settings                                  write settings (null = legacy source)
 *   GET  /api/tenant/branding                                  name + logo URL (any member, #2387)
 *   PUT  /api/tenant/branding                                  rename the tenant
 *   PUT  /api/tenant/branding/logo                             upload / replace the logo (raw bytes)
 *   DELETE /api/tenant/branding/logo                           remove the logo
 *
 * All routes are admin-only server-side, except the two branding reads. `http.*` returns the UNWRAPPED inner
 * `data` (ADR-0006 §5). Every shape comes from the generated OpenAPI types, so
 * a backend contract change fails compilation here. No GET response carries a
 * secret (only `last4`), and nothing here caches a submitted body.
 */

import api, { http } from '@/lib/api';
import type { components, paths } from '@/types/api.generated';

type JsonBody<T> = T extends { content: { 'application/json': infer B } } ? B : never;
type DataOf<T> = T extends { data: infer D } ? D : never;

export type TenantIntegrationStatus = components['schemas']['TenancyIntegrationStatus'];
export type IntegrationProvider = TenantIntegrationStatus['provider'];
export type IntegrationSource = TenantIntegrationStatus['source'];
export type ShopifyCredentialsBody = JsonBody<
  paths['/api/tenant/integrations/shopify']['put']['requestBody']
>;
export type ShopifyCredentialKind = ShopifyCredentialsBody['credentialKind'];
export type OnfleetCredentialsBody = JsonBody<
  paths['/api/tenant/integrations/onfleet']['put']['requestBody']
>;
export type WebhookRegistration = components['schemas']['TenancyWebhookRegistration'];
export type IntegrationWebhooks = components['schemas']['TenancyWebhookList'];

export type TenantSettings = DataOf<
  JsonBody<paths['/api/tenant/settings']['get']['responses'][200]>
>;
export type TenantSettingSource = TenantSettings['epochDate']['source'];
export type TenantSettingsBody = JsonBody<paths['/api/tenant/settings']['put']['requestBody']>;

export type TenantBranding = DataOf<
  JsonBody<paths['/api/tenant/branding']['get']['responses'][200]>
>;
export type TenantNameBody = JsonBody<paths['/api/tenant/branding']['put']['requestBody']>;

export const tenantIntegrationsAPI = {
  get(): Promise<TenantIntegrationStatus[]> {
    return http.get<TenantIntegrationStatus[]>('/api/tenant/integrations');
  },
  saveShopify(body: ShopifyCredentialsBody): Promise<TenantIntegrationStatus> {
    return http.put<TenantIntegrationStatus>('/api/tenant/integrations/shopify', body);
  },
  saveOnfleet(body: OnfleetCredentialsBody): Promise<TenantIntegrationStatus> {
    return http.put<TenantIntegrationStatus>('/api/tenant/integrations/onfleet', body);
  },
  registerWebhooks(provider: IntegrationProvider): Promise<WebhookRegistration> {
    return http.post<WebhookRegistration>(`/api/tenant/integrations/${provider}/register-webhooks`);
  },
  listWebhooks(provider: IntegrationProvider): Promise<IntegrationWebhooks> {
    return http.get<IntegrationWebhooks>(`/api/tenant/integrations/${provider}/webhooks`);
  },
};

export const tenantSettingsAPI = {
  get(): Promise<TenantSettings> {
    return http.get<TenantSettings>('/api/tenant/settings');
  },
  save(body: TenantSettingsBody): Promise<TenantSettings> {
    return http.put<TenantSettings>('/api/tenant/settings', body);
  },
};

const BRANDING_PATH = '/api/tenant/branding';
const LOGO_PATH = '/api/tenant/branding/logo';

export const tenantBrandingAPI = {
  get(): Promise<TenantBranding> {
    return http.get<TenantBranding>(BRANDING_PATH);
  },
  saveName(body: TenantNameBody): Promise<TenantBranding> {
    return http.put<TenantBranding>(BRANDING_PATH, body);
  },
  /** The body is the file's raw bytes; the server judges the type by its magic bytes. */
  uploadLogo(file: File): Promise<TenantBranding> {
    return http.put<TenantBranding>(LOGO_PATH, file, { headers: { 'Content-Type': file.type } });
  },
  deleteLogo(): Promise<TenantBranding> {
    return http.delete<TenantBranding>(LOGO_PATH);
  },
  /**
   * The logo bytes behind a `logoUrl`. The route is authenticated by the
   * bearer token, which an `<img src>` cannot send, so the image is fetched
   * here (the client attaches the token) and shown from an object URL.
   */
  async fetchLogo(logoUrl: string): Promise<Blob> {
    const response = await api.get<Blob>(logoUrl, { responseType: 'blob' });
    return response.data;
  },
};
